/**
 * TODO registry endpoints — all URLs include trailing slash to avoid
 * FastAPI 307 redirects.
 */

import type {
  TodoBatchResponse,
  TodoItem,
  TodoItemClaim,
  TodoItemCreate,
  TodoItemUpdate,
  TodoListFilters,
  TodoListResponse,
  TodoStatusTransition,
  TodoTriageMode,
  TodoTriageRequest,
} from '@/types/todo'
import type {
  TodoBulkTriageModeRequest,
  TodoBulkTriageModeResponse,
} from '@/types/todoBulkTriageMode'
import { apiClient } from './client'
import { idempotencyHeaders } from './idempotency'
import { batchIdsQuery } from './queryParams'

export const todos = {
  list: async (params?: TodoListFilters): Promise<TodoListResponse> => {
    const response = await apiClient.get<TodoListResponse>('/api/v1/todos/', {
      params,
    })
    return response.data
  },

  get: async (id: string): Promise<TodoItem> => {
    const response = await apiClient.get<TodoItem>(`/api/v1/todos/${id}/`)
    return response.data
  },

  getByNumber: async (n: number): Promise<TodoItem> => {
    const response = await apiClient.get<TodoItem>(`/api/v1/todos/by-number/${n}/`)
    return response.data
  },

  batch: async (ids: string[]): Promise<TodoBatchResponse> => {
    const response = await apiClient.get<TodoBatchResponse>(
      `/api/v1/todos/batch/?${batchIdsQuery(ids)}`
    )
    return response.data
  },

  /** With a key, a repeat of the same create answers the TODO the first one made. */
  create: async (
    data: TodoItemCreate,
    idempotencyKey?: string
  ): Promise<TodoItem> => {
    const response = await apiClient.post<TodoItem>(
      '/api/v1/todos/',
      data,
      idempotencyKey === undefined ? undefined : idempotencyHeaders(idempotencyKey)
    )
    return response.data
  },

  update: async (id: string, data: TodoItemUpdate): Promise<TodoItem> => {
    const response = await apiClient.put<TodoItem>(
      `/api/v1/todos/${id}/`,
      data
    )
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/todos/${id}/`)
  },

  transitionStatus: async (
    id: string,
    data: TodoStatusTransition
  ): Promise<TodoItem> => {
    const response = await apiClient.put<TodoItem>(
      `/api/v1/todos/${id}/status/`,
      data
    )
    return response.data
  },

  claim: async (id: string, data: TodoItemClaim): Promise<TodoItem> => {
    const response = await apiClient.post<TodoItem>(
      `/api/v1/todos/${id}/claim/`,
      data
    )
    return response.data
  },

  triage: async (id: string, data: TodoTriageRequest): Promise<TodoItem> => {
    const response = await apiClient.post<TodoItem>(`/api/v1/todos/${id}/triage/`, data)
    return response.data
  },

  setTriageModeBulk: async (
    todoIds: string[],
    triageMode: TodoTriageMode
  ): Promise<TodoBulkTriageModeResponse> => {
    const body: TodoBulkTriageModeRequest = {
      todo_ids: todoIds,
      triage_mode: triageMode,
    }
    const response = await apiClient.post<TodoBulkTriageModeResponse>(
      '/api/v1/todos/bulk/triage-mode/',
      body
    )
    return response.data
  },
}
